import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Mail, Phone, MapPin } from "lucide-react";

const KI_LOGO = "/images/ki-logo.png";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="py-6 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 text-[#A3A3A3] hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </Link>
            <Link to="/">
              <img src={KI_LOGO} alt="Knotts Investments LLC" className="h-12 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Title */}
          <div className="text-center mb-12">
            <Shield className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-[#A3A3A3]">Knotts Investments LLC</p>
          </div>

          {/* Content Sections */}
          <div className="space-y-8 text-[#E5E5E5]">
            <p className="text-lg leading-relaxed">
              Knotts Investments LLC respects your privacy and is committed to protecting your personal information.
            </p>

            <section>
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#D4AF37]"></span>
                Information We Collect
              </h2>
              <ul className="list-disc list-inside space-y-2 text-[#A3A3A3] ml-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Property address</li>
                <li>Any information submitted through our forms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#D4AF37]"></span>
                How We Use Your Information
              </h2>
              <ul className="list-disc list-inside space-y-2 text-[#A3A3A3] ml-4">
                <li>To contact you regarding your property</li>
                <li>To provide cash offer information</li>
                <li>To respond to inquiries</li>
                <li>To improve our services and marketing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#D4AF37]"></span>
                Information Sharing
              </h2>
              <p className="text-[#A3A3A3] leading-relaxed">
                We do not sell, trade, or share your personal information with third parties except as required by law or necessary to help complete a real estate transaction.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#D4AF37]"></span>
                SMS & Communications
              </h2>
              <p className="text-[#A3A3A3] leading-relaxed">
                By submitting your information, you consent to receive calls, emails, and text messages from Knotts Investments LLC regarding your inquiry. Message and data rates may apply.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#D4AF37]"></span>
                Data Security
              </h2>
              <p className="text-[#A3A3A3] leading-relaxed">
                We take reasonable precautions to protect your information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#D4AF37]"></span>
                Contact Information
              </h2>
              <div className="bg-[#141414] border border-white/10 p-6 space-y-3">
                <p className="text-white font-semibold">Knotts Investments LLC</p>
                <div className="flex items-center gap-3 text-[#A3A3A3]">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" />
                  <span>4111 Rose Lake Dr Ste E, Charlotte, NC 28217-2864</span>
                </div>
                <div className="flex items-center gap-3 text-[#A3A3A3]">
                  <Phone className="w-4 h-4 text-[#D4AF37]" />
                  <a href="tel:704-930-0403" className="hover:text-[#D4AF37] transition-colors">704-930-0403</a>
                </div>
                <div className="flex items-center gap-3 text-[#A3A3A3]">
                  <Mail className="w-4 h-4 text-[#D4AF37]" />
                  <a href="mailto:rknotts@knottsinvestments.com" className="hover:text-[#D4AF37] transition-colors">rknotts@knottsinvestments.com</a>
                </div>
              </div>
            </section>

            <p className="text-sm text-[#666] pt-8 border-t border-white/10">
              Last Updated: June 2026
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src={KI_LOGO} alt="Knotts Investments LLC" className="h-10 w-auto mx-auto mb-4" />
          <div className="flex justify-center gap-4 text-sm text-[#A3A3A3] mb-4">
            <Link to="/privacy-policy" className="hover:text-[#D4AF37] transition-colors">Privacy Policy</Link>
            <span>|</span>
            <Link to="/terms-conditions" className="hover:text-[#D4AF37] transition-colors">Terms & Conditions</Link>
          </div>
          <p className="text-[#666] text-sm">
            © {new Date().getFullYear()} Knotts Investments LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
